import { LitElement, css, html, nothing } from 'lit';
import { customElement, property } from 'lit/decorators.js';
import { i18n } from '../i18n/nl';
import { iconChatBubble, iconChevronDown } from './icons';

/**
 * `<embrace-chat-launcher>` — de zwevende ronde knop rechtsonder waarmee
 * de bezoeker het chat-paneel opent/sluit, plus de status- of unread-
 * indicator rechtsboven aan de knop.
 *
 * ## Props (puur signaal-input — geen state-logica hierin)
 *
 *   - `panelOpen` — drijft het icoon (chevron-down vs chat-bubble) en de
 *     aria-label van de knop.
 *   - `unreadCount` — count komt uit `selectUnreadOperatorCount(state)`.
 *     Als > 0 → rode badge (overrulet de online/offline-stip).
 *   - `online` — `true`=groene stip, `false`=grijze, `null`=geen indicator
 *     (service-status nog niet bekend).
 *
 * Geen aparte click-event — een normale `click` op de host volstaat,
 * de parent luistert daarop.
 *
 * ## Waarom losstaand
 *
 * De launcher had ~70 regels CSS + render-logica in `embrace-chat.ts`,
 * inclusief de unread-vs-online-overrule-keuze. Die keuze is nu één
 * pure `if (unreadCount > 0)` in deze component, plus een geteste
 * selector-functie. `embrace-chat.ts` houdt zo alleen launcher-`@click`-
 * routing over en geen indicator-state-rekenwerk meer.
 */
@customElement('embrace-chat-launcher')
export class EmbraceChatLauncher extends LitElement {
  @property({ type: Boolean })
  panelOpen = false;

  @property({ type: Number })
  unreadCount = 0;

  /** `null` = service-status onbekend → geen stip. */
  @property({ attribute: false })
  online: boolean | null = null;

  static override styles = css`
    :host {
      display: contents;
    }

    .launcher {
      position: fixed;
      right: var(--ec-offset-x);
      bottom: var(--ec-offset-y);
      z-index: var(--ec-z-index);

      width: var(--ec-launcher-size);
      height: var(--ec-launcher-size);
      border-radius: 9999px;
      border: 0;

      background: var(--ec-launcher-bg);
      color: #fff;
      box-shadow: var(--ec-shadow-launcher);
      cursor: pointer;

      display: grid;
      place-items: center;

      transition: var(--ec-transition-launcher-hover);
    }
    .launcher:hover {
      /* Subtiele schaal-feedback i.p.v. omhoog-spring. 1.05 = ~3px
         breder/hoger op een 60px-launcher — merkbaar maar niet onrustig. */
      transform: scale(1.05);
    }
    .launcher:active {
      transform: scale(1.02);
    }
    .launcher:focus-visible {
      outline: var(--ec-focus-ring-width) solid var(--ec-focus-ring);
      outline-offset: var(--ec-focus-ring-offset);
    }
    .launcher svg {
      width: 30px;
      height: 30px;
    }

    /**
     * Eén indicator die rechtsboven aan de launcher hangt en drie modi
     * heeft, mutually exclusive:
     *   - .online   : kleine groene stip (chatservice beschikbaar)
     *   - .offline  : kleine grijze stip (chatservice gesloten)
     *   - .unread   : grotere rode cirkel/pill met aantal ongelezen
     *                 operator-berichten (overrulet status — bezoeker
     *                 hoeft niet te weten of de service "online" is als
     *                 er sowieso al berichten op hem wachten)
     */
    .indicator {
      position: absolute;
      border-radius: 9999px;
      text-align: center;
      color: #fff;
      font: 700 10px/15px var(--ec-font-family);
      box-sizing: border-box;
      width: 22px;
      height: 22px;
      border: 2.5px solid var(--ec-primary);
      top: -2px;
      right: -2px;
    }
    .indicator.online { background: #22C55E; }
    .indicator.offline { background: #D1D1D2; }
    .indicator.unread {
      padding: 0 1px;
      background: #EF4444;
      line-height: 17px;
      /* Padding teruggeschroefd naar 1px zodat "9+" past binnen het
         smallere content-area (22 - 5 - 2 = 15px, "9+" ≈ 13px). */
    }

    /**
     * Mobile fullscreen (≤480px): paneel pakt de hele viewport (zie
     * chat-panel.ts), dus de launcher rechtsonder zou exact op de
     * composer-send-knop liggen. Verberg 'm in die situatie — de X
     * in de header neemt de close-functie over. Op desktop blijft
     * de launcher gewoon zichtbaar zodat bezoeker kan minimaliseren
     * naar de hoek.
     */
    @media (max-width: 480px) {
      :host([panelopen]) .launcher {
        display: none;
      }
    }
  `;

  override render() {
    return html`
      <button
        part="launcher"
        class="launcher"
        aria-label=${this.panelOpen
          ? i18n.misc.launcherMinimize
          : i18n.misc.launcherOpen}
        aria-expanded=${this.panelOpen}
      >
        ${this.panelOpen ? iconChevronDown() : iconChatBubble()}
        ${this.renderIndicator()}
      </button>
    `;
  }

  private renderIndicator() {
    if (this.unreadCount > 0) {
      const display = this.unreadCount > 9 ? '9+' : String(this.unreadCount);
      return html`<span
        class="indicator unread"
        part="launcher-indicator"
        aria-label=${i18n.misc.unreadAria(this.unreadCount)}
      >${display}</span>`;
    }
    if (this.online === null) return nothing;
    return html`<span
      class=${`indicator ${this.online ? 'online' : 'offline'}`}
      part="launcher-indicator"
      aria-label=${this.online ? i18n.misc.indicatorOnline : i18n.misc.indicatorOffline}
    ></span>`;
  }
}

declare global {
  interface HTMLElementTagNameMap {
    'embrace-chat-launcher': EmbraceChatLauncher;
  }
}
